import { useMemo, useCallback } from "react";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useCommandEntries } from "@/hooks/useCommandEntries";

// Generic command type for flexibility
interface CommandLike {
  id: string;
  number: string;
  status?: string | null;
  is_active?: boolean | null;
}

export function useCommandEntryValidation() {
  const { activeRestaurant } = useRestaurantContext();
  const { getActiveEntries } = useCommandEntries();
  
  const requireEntry = activeRestaurant?.require_command_entry ?? false;
  
  const activeEntries = useMemo(() => getActiveEntries(), [getActiveEntries]);
  
  const openedCommandNumbers = useMemo(() => {
    return new Set(activeEntries.map(e => e.command_number));
  }, [activeEntries]);
  
  // Returns true if the command has an active entry (opened)
  const hasActiveEntry = useCallback((commandNumber: string): boolean => {
    if (!requireEntry) return true;
    return openedCommandNumbers.has(commandNumber);
  }, [requireEntry, openedCommandNumbers]);
  
  // Returns commands that can be used (for display in PDV/Garcom)
  const getUsableCommands = useCallback(<T extends CommandLike>(allCommands: T[]): T[] => {
    if (!requireEntry) {
      // All active commands (or all if is_active not defined)
      return allCommands.filter(c => c.is_active !== false);
    }
    
    // Only commands that are in_use OR have an active entry
    return allCommands.filter(c => {
      if (c.is_active === false) return false;
      // If already in use, show it
      if (c.status === "in_use") return true;
      // Otherwise, only if has active entry
      return openedCommandNumbers.has(c.number);
    });
  }, [requireEntry, openedCommandNumbers]);
  
  // Get entry details for a command number
  const getEntryForCommand = useCallback((commandNumber: string) => {
    return activeEntries.find(e => e.command_number === commandNumber);
  }, [activeEntries]);
  
  return { 
    hasActiveEntry, 
    getUsableCommands, 
    getEntryForCommand,
    requireEntry,
    openedCommandNumbers
  };
}
